import React, { useRef, useEffect, useState } from "react";
import Chart from "chart.js";
import "chartjs-gauge";
import { Box } from "@material-ui/core";
import { GaugeChartDummyData } from "./dummyData/SingleDummyData";

/**
 * Gauge Chart
 * @param {*} datasets datasets
 * @param {function(Number): void} setAverageScoreValue 平均スコアセッター（連携用）
 * @param {function(string): void} setAverageScoreLabel 平均スコアラベルセッター（連携用）
 * @param {function(string): void} setAverageScoreColor 平均スコア色セッター（連携用）
 * @returns
 */
const GaugeChart = ({
  datasets = GaugeChartDummyData.datasets,
  setAverageScoreValue = (value) => {},
  setAverageScoreLabel = (value) => {},
  setAverageScoreColor = (value) => {},
}) => {
  const canvasRef = useRef(null);
  const [averageValue, setAverageValue] = useState(0);
  const [averageColor, setAverageColor] = useState("");
  const [averageLabel, setAverageLabel] = useState("");
  const chartInstanceRef = useRef(null);

  const darkenColor = (hex, factor = 0.7) => {
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    r = Math.floor(r * factor);
    g = Math.floor(g * factor);
    b = Math.floor(b * factor);
    return `rgb(${r},${g},${b})`;
  };

  useEffect(() => {
    try {
      const ctx = canvasRef.current.getContext("2d");

      if (chartInstanceRef.current) {
        chartInstanceRef.current.stop();
        chartInstanceRef.current.destroy();
      }

      datasets = JSON.parse(JSON.stringify(datasets));
      datasets = datasets.map((dataset) => {
        dataset.minValue = dataset.data[0]; //最小値
        dataset.data = dataset.data.slice(1); //最小値除外（※chartjs-gaugeの仕様考慮）
        return dataset;
      });

      chartInstanceRef.current = new Chart(ctx, {
        type: "gauge",
        data: {
          datasets: datasets,
        },
        options: {
          animation: {
            duration: 250,
          },
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 30,
              bottom: 0,
              left: 0,
              right: 0,
            },
          },
          legend: {
            display: true,
            position: "bottom",
            labels: {
              // 凡例ラベル
              generateLabels: (chart) => {
                try {
                  const newLabels = [];
                  chart.data.datasets.forEach((dataset, datasetIndex) => {
                    const values = [dataset.minValue, ...dataset.data];
                    const labels = dataset.labels;
                    const colors = dataset.backgroundColor;
                    for (let i = 0; i < values.length - 1; i++) {
                      if (
                        i + 1 > values.length - 1 ||
                        values[i + 1] - values[i] <= 1
                      ) {
                        continue;
                      }
                      newLabels.push({
                        text: `${labels[i]}`,
                        fillStyle: colors[i],
                      });
                    }
                  });
                  return newLabels.reverse();
                } catch (e) {
                  console.error(e);
                  return [];
                }
              },
              fontSize: 12,
            },
          },
          needle: {
            radiusPercentage: 2,
            widthPercentage: 3.2,
            lengthPercentage: 80,
            color: "black",
          },
          valueLabel: {
            display: false,
            formatter: (v) => v,
          },
          plugins: {
            // 外側ラベル
            datalabels: {
              display: true,
              anchor: "end",
              align: "end",
              offset: 3,
              font: {
                color: "#ffffff",
                size: 12,
              },
              formatter: (value, _ctx) => {
                try {
                  const index = _ctx.dataIndex;
                  const datasetIndex = _ctx.datasetIndex;
                  const dataset = _ctx.chart.data.datasets[datasetIndex];
                  const values = [dataset.minValue, ...dataset.data];
                  if (index + 1 > values.length - 1) return "";
                  const start = values[index];
                  const end = values[index + 1];
                  if (end - start <= 1) return "";
                  return `${start}–${end}`;
                } catch (e) {
                  console.error(e);
                  return "";
                }
              },
            },
            outlabels: {
              display: false,
            },
          },
        },
        plugins: [
          {
            // 動的値に応じた針メーター
            beforeDraw: (chart) => {
              try {
                chart.config.data.datasets.forEach((dataset, datasetIndex) => {
                  const values = [dataset.minValue, ...dataset.data];
                  const colors = dataset.backgroundColor;
                  const labels = dataset.labels;
                  const val = Number(dataset.value);
                  setAverageValue(val);
                  setAverageScoreValue(val);
                  let needleColor = "black";
                  for (let i = 0; i < values.length - 1; i++) {
                    if (
                      i + 1 > values.length - 1 ||
                      values[i + 1] - values[i] <= 1
                    ) {
                      continue;
                    }
                    if (val <= values[i + 1] && val >= values[i]) {
                      needleColor = darkenColor(colors[i], 0.9);
                      setAverageColor(colors[i]);
                      setAverageScoreColor(colors[i]);
                      setAverageLabel(labels[i]);
                      setAverageScoreLabel(labels[i]);
                      break;
                    }
                  }
                  chart.options.needle.color = needleColor;
                });
              } catch (e) {
                console.error(e);
              }
            },
          },
        ],
      });
    } catch (e) {
      console.error(e);
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.stop();
        chartInstanceRef.current.destroy();
      }
    };
  }, [datasets]);

  return (
    <Box style={{ width: "100%", height: "100%" }}>
      <Box
        style={{
          textAlign: "center",
          fontWeight: "bold",
          background: averageColor,
          width: "50%",
          margin: "0 auto",
          color: "#ffffff",
        }}
      >
        {averageValue}
        <br />
        {averageLabel}
      </Box>
      <canvas ref={canvasRef} />
    </Box>
  );
};

export default GaugeChart;
